import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCheck, 
  faExclamationTriangle, 
  faExclamationCircle, 
  faInfoCircle 
} from '@fortawesome/free-solid-svg-icons';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../hooks/useAuth';
import '../styles/ToastNotifications.css';

const ToastNotifications = () => {
  const [toasts, setToasts] = useState([]);
  const { user } = useAuth(); // Add authentication check
  const { socket } = useSocket();

  useEffect(() => {
    // Don't set up socket listeners if user is not authenticated
    if (!user || !socket) return;

    const handleNewNotification = (notification) => {
      // Check notification settings from localStorage
      const settings = JSON.parse(localStorage.getItem('notificationSettings') || '{}');
      const defaultSettings = {
        soundEnabled: true,
        toastEnabled: true,
        volume: 0.5,
        autoHide: true,
        hideDelay: 8000
      };
      const notificationSettings = { ...defaultSettings, ...settings };

      // Only create toast if toasts are enabled
      if (notificationSettings.toastEnabled) {
        const toast = {
          id: Date.now() + Math.random(),
          ...notification,
          timestamp: Date.now()
        };

        setToasts(prev => [...prev, toast]);

        // Auto-remove toast after specified delay (unless it's an error)
        if (notificationSettings.autoHide && notification.type !== 'error') {
          setTimeout(() => {
            removeToast(toast.id);
          }, notificationSettings.hideDelay);
        }
      }

      // Play notification sound if enabled
      if (notificationSettings.soundEnabled) {
        playNotificationSound(notification.type, notificationSettings.volume);
      }
    };

    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, user]); // Add user dependency

  const playNotificationSound = (type, volume = 0.5) => {
    try {
      // Create audio context for different notification sounds
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Different frequencies for different notification types
      const frequencies = {
        success: [523.25, 659.25], // C5, E5 - pleasant ascending
        warning: [440, 440, 440], // A4 repeated - attention grabbing
        error: [311.13, 311.13], // Eb4 repeated - urgent but not harsh
        info: [523.25] // C5 single tone - gentle
      };

      const freqs = frequencies[type] || frequencies.info;
      
      freqs.forEach((freq, index) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(volume * 0.1, audioContext.currentTime + 0.01);
          gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        }, index * 200);
      });
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  };

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return faCheck;
      case 'warning':
        return faExclamationTriangle;
      case 'error':
        return faExclamationCircle;
      default:
        return faInfoCircle;
    }
  };

  const getToastClass = (type) => {
    return `toast toast-${type}`;
  };

  // Don't render anything if user is not authenticated
  if (!user) {
    return null;
  }

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={getToastClass(toast.type)}
        >
          <div className="toast-content">
            <div className="toast-icon">
              <FontAwesomeIcon icon={getToastIcon(toast.type)} />
            </div>
            <div className="toast-text">
              <div className="toast-title">{toast.title}</div>
              <div className="toast-message">{toast.message}</div>
            </div>
            <button
              className="toast-close"
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="toast-progress">
            <div className="toast-progress-bar"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ToastNotifications;
